import React, { useCallback, useMemo } from "react";

import { useTranslation } from "react-i18next";
import { Bar, BarChart, LabelList, XAxis, YAxis } from "recharts";

import {
  ChartContainer,
  ChartLegend,
  ChartTooltip,
  ChartTooltipContent,
} from "@/src/components/ui/chart";

import {
  formatCount,
  formatRankedValue,
  getCategoryAxisWidth,
  getLastNonZeroKey,
  RANKED_DEFAULT_LIMIT,
  rankWithOthers,
  sumSeriesValues,
  toChartConfig,
  truncateLabel,
} from "./charts.utils";
import { endRoundedBarShape } from "./endRoundedBarShape";

import type { JSX } from "react";

import type { ChartSeries, RankedDatum } from "./charts.types";
import type { BarRadius } from "./endRoundedBarShape";

const ROW_HEIGHT = 36;
const LEGEND_HEIGHT = 32;
const MIN_RIGHT_MARGIN = 16;
// 21 * 7 + 16 = 163 <= 168, so a truncated label always fits the clamped axis.
const LABEL_MAX_LENGTH = 21;
const END_RADIUS: BarRadius = [0, 4, 4, 0];

interface RankedBarChartProps<TKey extends string> {
  dataList: readonly RankedDatum<TKey>[];
  seriesList: readonly ChartSeries<TKey>[];
  othersLabel: string;
  limit?: number;
}

function RankedBarChart<TKey extends string>({
  dataList,
  seriesList,
  othersLabel,
  limit = RANKED_DEFAULT_LIMIT,
}: RankedBarChartProps<TKey>): JSX.Element {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const config = useMemo(() => toChartConfig(seriesList), [seriesList]);
  const rowList = useMemo(() => {
    const keyList = seriesList.map((series) => series.key);
    const rankedList = rankWithOthers(dataList, keyList, limit, othersLabel);
    const grandTotal = rankedList.reduce(
      (sum, datum) => sum + sumSeriesValues(datum.values, keyList),
      0,
    );

    return rankedList.map((datum) => ({
      label: datum.label,
      ...datum.values,
      endSeriesKey: getLastNonZeroKey(datum.values, keyList),
      valueLabel: formatRankedValue(
        sumSeriesValues(datum.values, keyList),
        grandTotal,
        lang,
      ),
    }));
  }, [dataList, lang, limit, othersLabel, seriesList]);
  const rightMargin = useMemo(
    () =>
      Math.max(
        MIN_RIGHT_MARGIN,
        getCategoryAxisWidth(
          rowList.map((row) => row.valueLabel),
          Number.POSITIVE_INFINITY,
        ),
      ),
    [rowList],
  );
  const yAxisWidth = useMemo(
    () =>
      getCategoryAxisWidth(
        rowList.map((row) => row.label),
        LABEL_MAX_LENGTH,
      ),
    [rowList],
  );
  const handleFormatValue = useCallback(
    (value: number) => formatCount(value, lang),
    [lang],
  );
  const isStacked = seriesList.length > 1;
  const height = rowList.length * ROW_HEIGHT + (isStacked ? LEGEND_HEIGHT : 0);

  return (
    <ChartContainer
      className="aspect-auto w-full"
      config={config}
      style={{ height }}
    >
      <BarChart
        accessibilityLayer
        data={rowList}
        layout="vertical"
        margin={{ left: 0, right: rightMargin }}
      >
        <XAxis
          hide
          allowDecimals={false}
          domain={[0, "dataMax"]}
          type="number"
        />
        <YAxis
          axisLine={false}
          dataKey="label"
          tickLine={false}
          type="category"
          width={yAxisWidth}
          tickFormatter={(label: string) =>
            truncateLabel(label, LABEL_MAX_LENGTH)
          }
        />
        <ChartTooltip
          content={<ChartTooltipContent valueFormatter={handleFormatValue} />}
          cursor={false}
        />
        {isStacked ? <ChartLegend /> : null}
        {seriesList.map((series) => (
          <Bar
            key={series.key}
            dataKey={series.key}
            fill={`var(--color-${series.key})`}
            shape={endRoundedBarShape(rowList, series.key, END_RADIUS)}
            stackId="ranked"
            stroke={isStacked ? "var(--card)" : undefined}
          >
            <LabelList
              className="tabular-nums"
              fontSize={12}
              position="right"
              style={{ fill: "var(--muted-foreground)" }}
              valueAccessor={(_entry, index) =>
                rowList[index]?.endSeriesKey === series.key
                  ? rowList[index].valueLabel
                  : undefined
              }
            />
          </Bar>
        ))}
      </BarChart>
    </ChartContainer>
  );
}

export default RankedBarChart;
